import React from 'react'
import {cn} from './cn'

type TypographyVariant =
    | 'display'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'body-large'
    | 'body'
    | 'body-small'
    | 'caption'
    | 'overline'

type TypographyColor =
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'muted'

interface TypographyProps {
    variant?: TypographyVariant
    color?: TypographyColor
    numeric?: boolean
    className?: string
    style?: React.CSSProperties
    children: React.ReactNode
    as?: keyof React.JSX.IntrinsicElements

    [key: string]: any
}

const variantElementMap: Record<TypographyVariant, keyof React.JSX.IntrinsicElements> = {
    'display': 'h1',
    'h1': 'h1',
    'h2': 'h2',
    'h3': 'h3',
    'h4': 'h4',
    'body-large': 'p',
    'body': 'p',
    'body-small': 'p',
    'caption': 'span',
    'overline': 'span',
}

const variantStyles: Record<TypographyVariant, string> = {
    'display': [
        'text-display font-extrabold leading-tight tracking-tight',
        'text-4xl sm:text-5xl lg:text-6xl xl:text-display'].join(' '),

    'h1': [
        'text-h1 font-bold leading-tight tracking-tight',
        'text-2xl sm:text-3xl lg:text-4xl xl:text-h1'].join(' '),

    'h2': [
        'text-h2 font-semibold leading-tight tracking-tight',
        'text-xl sm:text-2xl lg:text-3xl xl:text-h2'].join(' '),

    'h3': [
        'text-h3 font-semibold leading-normal',
        'text-lg sm:text-xl lg:text-2xl xl:text-h3'].join(' '),

    'h4': [
        'text-h4 font-medium leading-normal',
        'text-base sm:text-lg lg:text-xl xl:text-h4'].join(' '),

    'body-large': [
        'text-body-large font-normal leading-relaxed',
        'text-base sm:text-lg xl:text-body-large'].join(' '),

    'body': [
        'text-body font-normal leading-normal',
        'text-sm sm:text-base xl:text-body'].join(' '),

    'body-small': [
        'text-body-small font-normal leading-normal',
        'text-xs sm:text-sm xl:text-body-small'].join(' '),

    'caption': [
        'text-caption font-normal leading-tight',
        'text-xs xl:text-caption'].join(' '),

    'overline': [
        'text-overline font-medium leading-tight tracking-wider uppercase',
        'text-xs xl:text-overline'].join(' '),
}

const colorStyles: Record<TypographyColor, string> = {
    'default': 'text-gray-900 dark:text-white',
    'primary': 'text-primary-600 dark:text-primary-400',
    'secondary': 'text-secondary-600 dark:text-secondary-400',
    'success': 'text-success-600 dark:text-success-400',
    'warning': 'text-warning-600 dark:text-warning-400',
    'error': 'text-error-600 dark:text-error-400',
    'muted': 'text-gray-600 dark:text-gray-400',
}

export const Typography: React.FC<TypographyProps> = ({
                                                          variant = 'body',
                                                          color = 'default',
                                                          numeric = false,
                                                          className,
                                                          children,
                                                          as,
                                                          ...props
                                                      }) => {
    const Component = as || variantElementMap[variant]

    const classes = cn(
        'font-sans', variantStyles[variant],
        colorStyles[color],
        numeric && 'font-mono tabular-nums', className
    )

    return (
        <Component className={classes} {...props}>
            {children}
        </Component>
    )
}

Typography.displayName = 'Typography'

export const H2: React.FC<Omit<TypographyProps, 'variant'> & { children: React.ReactNode }> = (props) => (
    <Typography variant="h2" {...props} />
)

export const H3: React.FC<Omit<TypographyProps, 'variant'> & { children: React.ReactNode }> = (props) => (
    <Typography variant="h3" {...props} />
)

export const Body: React.FC<Omit<TypographyProps, 'variant'> & { children: React.ReactNode }> = (props) => (
    <Typography variant="body" {...props} />
)

export const BodySmall: React.FC<Omit<TypographyProps, 'variant'> & { children: React.ReactNode }> = (props) => (
    <Typography variant="body-small" {...props} />
)
